import { useCallback, useState } from "react";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";
import { StatCard } from "../../shared/components/StatCard";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../shared/components/ui/select";
import { BulkActionBar } from "./components/BulkActionBar";
import { BulkApprovalDialog, type BulkApprovalResult } from "./components/BulkApprovalDialog";
import { BulkRejectionDialog, type BulkRejectionResult } from "./components/BulkRejectionDialog";
import { SelectableSubmissionCard } from "./components/SelectableSubmissionCard";
import { BULK_OPERATION_MAX_ITEMS, useBulkOperations } from "./hooks/useBulkOperations";
import { useSubmissions, type Submission } from "./hooks/useSubmissions";

/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */

// Admin review queue only shows submitted content (PENDING and beyond)
// DRAFT submissions remain private to the content provider
type SubmissionStatus = "PENDING" | "APPROVED" | "REJECTED" | "PUBLISHED";
type BulkActionMode = "approve" | "reject" | null;

/**
 * Calculate start of today in local timezone
 */
function getStartOfToday(): Date {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return startOfToday;
}

/**
 * Calculate dashboard statistics from all submissions
 */
function calculateStatistics(
  allSubmissions: Array<{
    status: string;
    approvedAt?: string | null;
    rejectedAt?: string | null;
  }>,
) {
  const pendingCount = allSubmissions.filter((s) => s.status === "PENDING").length;
  const startOfToday = getStartOfToday();

  const approvedTodayCount = allSubmissions.filter((s) => {
    if (!s.approvedAt || s.status !== "APPROVED") return false;
    const approvedDate = new Date(s.approvedAt);
    return approvedDate >= startOfToday;
  }).length;

  const rejectedTodayCount = allSubmissions.filter((s) => {
    if (!s.rejectedAt || s.status !== "REJECTED") return false;
    const rejectedDate = new Date(s.rejectedAt);
    return rejectedDate >= startOfToday;
  }).length;

  return { pendingCount, approvedTodayCount, rejectedTodayCount };
}

/**
 * Submission List Content Component
 * Handles loading, error, empty, and list states with optional selection support
 */
function SubmissionListContent({
  loading,
  error,
  submissions,
  selectedTab,
  selectedIds,
  onToggleSelect,
}: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly submissions: Submission[];
  readonly selectedTab: "pending" | "all";
  readonly selectedIds: Set<string>;
  readonly onToggleSelect: (id: string) => void;
}) {
  // Only show selection UI on Pending tab (bulk actions only apply to pending submissions)
  const isSelectionMode = selectedTab === "pending";

  if (loading) {
    return (
      <div className="bg-none border border-neutral-800 rounded-lg p-12 text-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-none border border-neutral-800 rounded-lg p-12 text-center">
        <div className="text-red-400">Error: {error}</div>
      </div>
    );
  }

  if (submissions.length > 0) {
    return (
      <div className="space-y-4">
        {submissions.map((submission) => (
          <SelectableSubmissionCard
            key={submission.id}
            submission={submission}
            isSelected={selectedIds.has(submission.id)}
            isSelectionMode={isSelectionMode}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-none border border-neutral-800 rounded-lg p-12 text-center">
      <div className="max-w-md mx-auto space-y-4">
        <div className="w-16 h-16 bg-neutral-800 rounded-full mx-auto flex items-center justify-center">
          <svg className="w-8 h-8 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-white">All caught up!</h3>
        <p className="text-neutral-400">
          {selectedTab === "pending"
            ? "No submissions are waiting for review at the moment."
            : "No submissions match the selected filter."}
        </p>
      </div>
    </div>
  );
}

/**
 * Admin Review Queue - Dashboard for reviewing content submissions
 * Fetches real submissions from API with filters
 * Supports bulk approval/rejection operations
 */
export function ReviewQueue() {
  const [selectedTab, setSelectedTab] = useState<"pending" | "all">("pending");
  const [selectedStatus, setSelectedStatus] = useState<SubmissionStatus | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionMode, setBulkActionMode] = useState<BulkActionMode>(null);

  // Bulk operations hook
  const { bulkApprove, bulkReject, loading: bulkLoading } = useBulkOperations();

  // Fetch ALL submissions for statistics
  // Note: We exclude DRAFT submissions from the admin review queue entirely,
  // as drafts should only be visible to the content provider who created them
  const { submissions: allSubmissions, refetch: refetchAll } = useSubmissions({});

  // Fetch submissions based on current filter state for display
  const statusFilter = selectedTab === "pending" ? "PENDING" : selectedStatus;
  const { submissions, loading, error, refetch } = useSubmissions({
    status: statusFilter,
  });

  // Filter out DRAFT submissions from admin view
  const submissionsWithoutDrafts = submissions.filter((s) => s.status !== "DRAFT");
  const allSubmissionsWithoutDrafts = allSubmissions.filter((s) => s.status !== "DRAFT");

  // Calculate statistics from ALL submissions (excluding drafts)
  const { pendingCount, approvedTodayCount, rejectedTodayCount } = calculateStatistics(allSubmissionsWithoutDrafts);

  // Only allow bulk operations on PENDING submissions
  const selectableSubmissions = submissionsWithoutDrafts.filter((s) => s.status === "PENDING");

  const statusFilters: Array<{ label: string; value: string }> = [
    { label: "All Statuses", value: "all" },
    { label: "Pending", value: "PENDING" },
    { label: "Approved", value: "APPROVED" },
    { label: "Rejected", value: "REJECTED" },
    { label: "Published", value: "PUBLISHED" },
  ];

  // Selection handlers
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        // Enforce max selection limit
        if (newSet.size >= BULK_OPERATION_MAX_ITEMS) {
          return prev;
        }
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const idsToSelect = selectableSubmissions.slice(0, BULK_OPERATION_MAX_ITEMS).map((s) => s.id);
    setSelectedIds(new Set(idsToSelect));
  }, [selectableSubmissions]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Bulk operation handlers
  const handleBulkApprove = useCallback(
    async (notes: string, publishImmediately: boolean): Promise<BulkApprovalResult> => {
      const result = await bulkApprove({
        submissionIds: Array.from(selectedIds),
        notes: notes || undefined,
        publishImmediately,
      });
      return {
        approved: result.success,
        failed: result.failed,
      };
    },
    [bulkApprove, selectedIds],
  );

  const handleBulkReject = useCallback(
    async (reason: string, notes: string): Promise<BulkRejectionResult> => {
      const result = await bulkReject({
        submissionIds: Array.from(selectedIds),
        reason,
        notes: notes || undefined,
      });
      return {
        rejected: result.success,
        failed: result.failed,
      };
    },
    [bulkReject, selectedIds],
  );

  const handleBulkOperationComplete = useCallback(() => {
    setBulkActionMode(null);
    setSelectedIds(new Set());
    refetch();
    refetchAll();
  }, [refetch, refetchAll]);

  return (
    <div className="space-y-8 py-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Review Queue</h1>
        <p className="text-sm text-neutral-400 pb-6">Review and moderate content provider submissions</p>
      </div>

      {/* Queue Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Pending Reviews" value={loading ? "-" : pendingCount} />
        <StatCard label="Approved Today" value={loading ? "-" : approvedTodayCount} />
        <StatCard label="Rejected Today" value={loading ? "-" : rejectedTodayCount} />
        <StatCard label="Avg Review Time" value="-" />
      </div>

      {/* Bulk Action Bar - Show when items are selected */}
      {selectedIds.size > 0 && selectedTab === "pending" && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          totalCount={selectableSubmissions.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          onApprove={() => setBulkActionMode("approve")}
          onReject={() => setBulkActionMode("reject")}
          disabled={bulkLoading}
        />
      )}

      {/* Tabs: Pending / All */}
      <div className="flex items-center justify-between border-b border-neutral-800">
        <div className="flex gap-2">
          <button
            onClick={() => {
              setSelectedTab("pending");
              setSelectedStatus(undefined);
              setSelectedIds(new Set()); // Clear selection when changing tabs
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              selectedTab === "pending"
                ? "text-white border-b-2 border-lumi-accent"
                : "text-neutral-400 hover:text-white"
            }`}
          >
            Pending ({loading ? "-" : pendingCount})
          </button>
          <button
            onClick={() => {
              setSelectedTab("all");
              setSelectedStatus(undefined);
              setSelectedIds(new Set()); // Clear selection when changing tabs
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              selectedTab === "all" ? "text-white border-b-2 border-lumi-accent" : "text-neutral-400 hover:text-white"
            }`}
          >
            All Submissions
          </button>
        </div>

        {/* Status Filter - Only show in "All" tab */}
        {selectedTab === "all" && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-neutral-500">Filter:</span>
            <Select
              value={selectedStatus ?? "all"}
              onValueChange={(value) => setSelectedStatus(value === "all" ? undefined : (value as SubmissionStatus))}
            >
              <SelectTrigger className="w-[180px]">
                <span>{statusFilters.find((f) => f.value === (selectedStatus ?? "all"))?.label || "All Statuses"}</span>
              </SelectTrigger>
              <SelectContent>
                {statusFilters.map((filter) => (
                  <SelectItem key={filter.label} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Submissions List */}
      <SubmissionListContent
        loading={loading}
        error={error}
        submissions={submissionsWithoutDrafts}
        selectedTab={selectedTab}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
      />

      {/* Bulk Approval Dialog */}
      <BulkApprovalDialog
        isOpen={bulkActionMode === "approve"}
        submissionIds={Array.from(selectedIds)}
        submissions={submissionsWithoutDrafts}
        onConfirm={handleBulkApprove}
        onCancel={() => setBulkActionMode(null)}
        onComplete={handleBulkOperationComplete}
      />

      {/* Bulk Rejection Dialog */}
      <BulkRejectionDialog
        isOpen={bulkActionMode === "reject"}
        submissionIds={Array.from(selectedIds)}
        submissions={submissionsWithoutDrafts}
        onConfirm={handleBulkReject}
        onCancel={() => setBulkActionMode(null)}
        onComplete={handleBulkOperationComplete}
      />
    </div>
  );
}
